// Mock data for Professor Tamvada's Portfolio

export const professorData = {
  name: "Professor Jagannadha Pawan Tamvada",
  title: "Strategic Growth Advisor | Complex Problem Solver",
  subtitle: "Trusted advisor to governments, enterprises, and high-impact ventures worldwide",
  tagline: "Transforming complexity into strategic clarity for leaders navigating critical growth challenges",
  email: "j.tamvada@kingston.ac.uk",
  phone: "+44-789-9676924",
  linkedIn: "https://www.linkedin.com/in/jagtamvada/?originalSubdomain=uk",
  orcid: "https://orcid.org/0000-0002-1225-3174",
  calendlyLink: "https://calendly.com/jptamvada-ich0/30min",
  profileImage: "https://www.kingston.ac.uk/about/staff/professor-jagannadha-pawan-tamvada",
  currentPosition: "Professor of Entrepreneurship, Kingston University London",
  location: "London, United Kingdom"
};

export const aboutContent = {
  overview: "With over two decades of international experience spanning academia, government advisory, and enterprise leadership, I bring a unique systems-thinking approach to solving complex strategic challenges. My work sits at the intersection of entrepreneurship, innovation, and sustainable growth—advising leaders who face decisions that shape industries, economies, and societies.",
  
  philosophy: [
    {
      title: "Systems-Level Thinking",
      description: "I approach problems not as isolated challenges, but as interconnected systems requiring holistic solutions that address root causes and create lasting impact."
    },
    {
      title: "Evidence-Based Strategy",
      description: "Decades of rigorous research inform every strategic recommendation, ensuring decisions are grounded in empirical evidence and proven frameworks."
    },
    {
      title: "Global Perspective",
      description: "Having worked across continents—from Max Planck Institute in Germany to Harvard Business School in the US, and from UK to India—I bring cross-cultural insights that transcend local limitations."
    },
    {
      title: "Long-Term Value Creation",
      description: "Strategic guidance focused on sustainable growth, responsible innovation, and creating value that extends beyond immediate returns to societal impact."
    }
  ],
  
  credentials: [
    "PhD Economics (summa cum laude), University of Göttingen, Germany",
    "Otto-Hahn Medal, Max Planck Society",
    "Inaugural DRUID Best Dissertation Award",
    "Kauffman Foundation Best Paper Award, Academy of Management",
    "Fellow, Royal Society of Arts",
    "Steward, Council for Inclusive Capitalism with the Vatican"
  ],
  
  experience: [
    {
      institution: "Kingston University London",
      role: "Professor of Entrepreneurship & Director, RISE Research Hub",
      period: "2024 - Present"
    },
    {
      institution: "University of Southampton",
      role: "Associate Professor, Strategic Leadership",
      period: "2013 - 2023"
    },
    {
      institution: "Max Planck Institute of Economics",
      role: "Senior Research Fellow",
      period: "2004 - 2009"
    },
    {
      institution: "Harvard Business School",
      role: "Visiting Fellow",
      period: "2009"
    }
  ]
};

export const services = [
  {
    id: 1,
    title: "Strategic Growth Advisory",
    subtitle: "For scale-ups, enterprises, and institutional leaders",
    description: "Navigate complex growth decisions with confidence. I work with organizations facing inflection points—whether scaling operations, entering new markets, or transforming business models—to develop strategies that balance ambition with sustainable execution.",
    
    challenges: [
      "Scaling operations while maintaining organizational integrity",
      "Market expansion and international growth strategies",
      "Business model innovation and transformation",
      "Navigating regulatory complexity in growth phases"
    ],
    
    outcomes: [
      "Clear strategic roadmaps aligned with organizational capacity",
      "Evidence-based growth frameworks",
      "Risk-mitigation strategies for high-stakes decisions",
      "Sustainable competitive positioning"
    ],
    
    idealFor: "CEOs, Founders, Board Members, Institutional Leaders"
  },
  
  {
    id: 2,
    title: "Complex Problem Solving & Systems Design",
    subtitle: "For organizations facing multi-dimensional challenges",
    description: "Some challenges resist conventional solutions because they're symptoms of deeper systemic issues. I help leaders diagnose root causes, map interdependencies, and design interventions that create cascading positive effects across organizational and economic systems.",
    
    challenges: [
      "Persistent organizational challenges resistant to standard solutions",
      "Cross-functional problems requiring systems-level intervention",
      "Innovation bottlenecks and capability gaps",
      "Balancing competing stakeholder demands"
    ],
    
    outcomes: [
      "Systemic diagnosis revealing hidden constraints",
      "Multi-level intervention strategies",
      "Framework for ongoing adaptive problem-solving",
      "Alignment of organizational architecture with strategic goals"
    ],
    
    idealFor: "Enterprise Leaders, Innovation Directors, Strategy Teams"
  },
  
  {
    id: 3,
    title: "Government & Institutional Advisory",
    subtitle: "For policymakers and public sector leaders",
    description: "Public policy and institutional decisions carry profound societal implications. I advise governments and institutions on evidence-based policy design, economic development strategies, and institutional innovation—drawing on research spanning multiple countries and economic contexts.",
    
    challenges: [
      "Economic development and entrepreneurship ecosystem design",
      "Policy evaluation and impact assessment",
      "Institutional innovation and public sector transformation",
      "Technology adoption in governance and public services"
    ],
    
    outcomes: [
      "Evidence-based policy frameworks",
      "Institutional capacity building strategies",
      "Public-private collaboration models",
      "Measurable impact assessment methodologies"
    ],
    
    idealFor: "Government Leaders, Policymakers, Public Institutions, Development Organizations"
  },
  
  {
    id: 4,
    title: "Executive Mentorship & Leadership Guidance",
    subtitle: "For senior leaders navigating critical transitions",
    description: "Leadership at the highest levels requires both strategic clarity and personal resilience. I mentor senior executives through major transitions—whether assuming new roles, navigating organizational change, or making decisions that will define their legacy.",
    
    challenges: [
      "C-suite transitions and role evolution",
      "Leading through organizational transformation",
      "Balancing short-term pressures with long-term vision",
      "Developing strategic thinking capabilities"
    ],
    
    outcomes: [
      "Enhanced strategic decision-making frameworks",
      "Leadership presence and executive judgment",
      "Confidential sounding board for critical decisions",
      "Long-term leadership development trajectory"
    ],
    
    idealFor: "CEOs, Board Members, Senior Executives, Emerging Leaders"
  },
  
  {
    id: 5,
    title: "Venture & Enterprise Strategy",
    subtitle: "For high-growth ventures and corporate innovation",
    description: "High-growth ventures face unique strategic challenges—balancing rapid scaling with sustainable foundation-building. I advise founders and corporate innovation leaders on strategic positioning, resource allocation, and building ventures that can transition from startup to enduring enterprise.",
    
    challenges: [
      "Strategic positioning in competitive markets",
      "Venture scaling and operational excellence",
      "Innovation portfolio management",
      "Strategic partnerships and ecosystem development"
    ],
    
    outcomes: [
      "Clear strategic positioning and competitive advantage",
      "Scalable operational frameworks",
      "Evidence-based resource allocation strategies",
      "Innovation governance and portfolio management"
    ],
    
    idealFor: "Venture Founders, Corporate Innovation Leaders, Investment Partners"
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Dr. Sarah Mitchell",
    role: "Chief Executive Officer",
    organization: "TechVentures Global",
    image: "/api/placeholder/80/80",
    quote: "Professor Tamvada's strategic guidance was instrumental in navigating our most critical growth phase. His ability to see patterns across complex systems and distill them into actionable strategies is unparalleled. The frameworks he developed continue to guide our decision-making two years later.",
    outcome: "Successfully scaled operations across 12 markets"
  },
  {
    id: 2,
    name: "James Anderton",
    role: "Director of Innovation",
    organization: "UK Department for Business & Trade",
    image: "/api/placeholder/80/80",
    quote: "Working with Professor Tamvada transformed how we approach policy design. His evidence-based methodology and global perspective helped us develop an entrepreneurship ecosystem framework that's now being implemented nationwide. He doesn't just advise—he builds capacity.",
    outcome: "Policy framework adopted across 8 regional hubs"
  },
  {
    id: 3,
    name: "Priya Sharma",
    role: "Founder & CEO",
    organization: "SustainTech Ventures",
    image: "/api/placeholder/80/80",
    quote: "At a critical juncture where we faced competing strategic paths, Professor Tamvada provided the clarity we needed. His systems-thinking approach revealed constraints we hadn't recognized and opportunities we'd overlooked. The strategic roadmap he helped us develop became our blueprint for sustainable growth.",
    outcome: "Revenue growth 340% over 18 months"
  },
  {
    id: 4,
    name: "Michael Chen",
    role: "Board Member",
    organization: "Global Innovation Partners",
    image: "/api/placeholder/80/80",
    quote: "Professor Tamvada brings rare intellectual depth combined with practical wisdom. His insights during our board strategic reviews have consistently challenged our assumptions and elevated our thinking. He's become our trusted advisor for decisions that will shape our next decade.",
    outcome: "Strategic repositioning led to successful IPO"
  },
  {
    id: 5,
    name: "Dr. Anand Krishnan",
    role: "Vice Chancellor",
    organization: "National Institute for Policy Research",
    image: "/api/placeholder/80/80",
    quote: "Few advisors combine Professor Tamvada's academic rigor with real-world impact orientation. His work on institutional innovation and economic development has influenced policy at the highest levels. He understands both theory and practice—and more importantly, how to bridge them.",
    outcome: "Research-to-policy framework recognized internationally"
  },
  {
    id: 6,
    name: "Elizabeth Thompson",
    role: "Managing Director",
    organization: "Heritage Capital Group",
    image: "/api/placeholder/80/80",
    quote: "When evaluating complex investment opportunities, we turn to Professor Tamvada for strategic due diligence that goes beyond financial metrics. His ability to assess organizational systems, leadership capacity, and long-term viability has helped us avoid costly mistakes and identify hidden value.",
    outcome: "Portfolio performance improved by 28% year-over-year"
  }
];

export const recentMedia = {
  videos: [
    {
      id: 1,
      title: "Strategic Insights on Business Growth and Innovation",
      url: "https://www.youtube.com/watch?v=m9q_mCFKtKc",
      embedUrl: "https://www.youtube.com/embed/m9q_mCFKtKc",
      thumbnail: "https://img.youtube.com/vi/m9q_mCFKtKc/maxresdefault.jpg",
      description: "Expert discussion on strategic approaches to business growth and innovation in complex markets",
      date: "2024"
    },
    {
      id: 2,
      title: "Leadership and Entrepreneurship Perspectives",
      url: "https://www.youtube.com/watch?v=rvlxdLTxQwk",
      embedUrl: "https://www.youtube.com/embed/rvlxdLTxQwk",
      thumbnail: "https://img.youtube.com/vi/rvlxdLTxQwk/maxresdefault.jpg",
      description: "Insights on leadership challenges and entrepreneurial decision-making",
      date: "2024"
    }
  ],
  featuredArticle: {
    title: "The quiet rise in the tax burden for UK businesses will hit workers and consumers too",
    url: "https://theconversation.com/the-quiet-rise-in-the-tax-burden-for-uk-businesses-will-hit-workers-and-consumers-too-271965",
    publication: "The Conversation",
    date: "2024-12",
    excerpt: "Analysis of the increasing tax burden on UK businesses and its broader economic implications for workers and consumers.",
    category: "Economic Policy"
  }
};

export const publications = [
  {
    id: 1,
    title: "The quiet rise in the tax burden for UK businesses will hit workers and consumers too",
    type: "strategic-insight",
    category: "Governance",
    date: "2024-12",
    excerpt: "The UK's rising tax burden on businesses extends beyond corporate balance sheets to affect workers and consumers. This analysis examines the cascading economic effects of increased business taxation and explores policy alternatives that balance fiscal needs with economic dynamism.",
    readTime: "8 min read",
    featured: true,
    journal: "The Conversation",
    externalLink: "https://theconversation.com/the-quiet-rise-in-the-tax-burden-for-uk-businesses-will-hit-workers-and-consumers-too-271965"
  },
  {
    id: 2,
    title: "The Paradox of Incentives: Why Rational Economics Fails Complex Systems",
    type: "strategic-insight",
    category: "Strategy",
    date: "2024-10",
    excerpt: "Traditional incentive structures, designed for rational economic behavior, often produce counterintuitive outcomes in complex organizational systems. This essay explores why well-intentioned reward systems can undermine the very behaviors they aim to encourage, and presents a framework for designing incentives that account for systemic complexity.",
    readTime: "12 min read",
    featured: true,
    journal: "Business & Society",
    citation: "Tamvada, J.P. (2023). The irrationality of rationality in market economics: A paradox of incentives perspective. Business & Society, 62(3), 482-487."
  },
  {
    id: 2,
    title: "From Entrepreneurship to Leadership: Strategic Transitions at Scale",
    type: "research-article",
    category: "Leadership",
    date: "2024-09",
    excerpt: "The transition from entrepreneurial founder to strategic leader represents one of the most challenging inflection points in organizational growth. Drawing on longitudinal research and practical cases, this piece examines what it takes to evolve from opportunity creation to institutional building—and why many fail to make the leap.",
    readTime: "15 min read",
    featured: true,
    journal: "The Journal of Technology Transfer",
    citation: "Audretsch, D. & Tamvada, J.P. (2023). From entrepreneurship to leadership. The Journal of Technology Transfer, 48, 814-820."
  },
  {
    id: 3,
    title: "Creating Effective Innovation Ecosystems: A Commitment Systems Perspective",
    type: "research-article",
    category: "Innovation",
    date: "2024-08",
    excerpt: "Innovation ecosystems fail not from lack of resources, but from misalignment of commitments among stakeholders. This research introduces a commitment system framework that explains why some ecosystems thrive while others, despite comparable resources, struggle—and provides actionable guidance for ecosystem architects.",
    readTime: "18 min read",
    featured: false,
    journal: "The Journal of Technology Transfer",
    citation: "Audretsch, D.B., Khurana, I., Dutta, D.K. & Tamvada, J.P. (2024). Creating effective university innovation and entrepreneurial ecosystems: A commitment system perspective."
  },
  {
    id: 4,
    title: "Technology Adoption in Emerging Markets: Beyond the Innovation Narrative",
    type: "strategic-insight",
    category: "Growth",
    date: "2024-07",
    excerpt: "The prevailing narrative around technology adoption in emerging markets overlooks critical institutional and cultural factors that determine success or failure. Based on extensive research across rural India and other developing contexts, this essay challenges conventional wisdom about technology diffusion and offers frameworks for responsible innovation in complex institutional environments.",
    readTime: "14 min read",
    featured: false,
    journal: "Technological Forecasting and Social Change",
    citation: "Multiple publications in Technological Forecasting and Social Change (2023)"
  },
  {
    id: 5,
    title: "The Systems View of Strategic Growth: Integration Over Optimization",
    type: "framework",
    category: "Strategy",
    date: "2024-06",
    excerpt: "Organizations pursuing growth often optimize individual functions at the expense of systemic coherence. This framework presents a systems-based approach to strategic growth where integration trumps optimization—showing how to build organizational capabilities that scale without fragmenting.",
    readTime: "16 min read",
    featured: false
  },
  {
    id: 6,
    title: "Beyond GDP: Valuing Societal Progress in Economic Decision-Making",
    type: "research-article",
    category: "Governance",
    date: "2024-05",
    excerpt: "Economic orthodoxy's fixation on GDP obscures more meaningful measures of societal progress. This interdisciplinary research proposes alternative frameworks for valuing human development, health outcomes, and long-term sustainability—with implications for both policy and enterprise strategy.",
    readTime: "20 min read",
    featured: false,
    journal: "Acta Paediatrica",
    citation: "Meka, K., Jacob, C.M., Modi, N., Tamvada, J.P., et al. (2023). Valuing maternal, newborn, child and adolescent health for societal progress."
  },
  {
    id: 7,
    title: "Navigating Complexity: When Standard Consulting Frameworks Fail",
    type: "strategic-insight",
    category: "Complexity",
    date: "2024-04",
    excerpt: "Standard consulting frameworks—2x2 matrices, five forces, BCG growth-share—provide comfortable simplification of strategic challenges. But complex problems resist this reductionism. This piece examines when traditional frameworks mislead rather than illuminate, and introduces approaches better suited to genuine complexity.",
    readTime: "11 min read",
    featured: false
  },
  {
    id: 8,
    title: "Inclusive Capitalism: From Corporate Rhetoric to Systemic Change",
    type: "perspective",
    category: "Governance",
    date: "2024-03",
    excerpt: "The Council for Inclusive Capitalism brings together global leaders committed to stakeholder capitalism. But translating commitment into action requires more than declarations—it demands fundamental rethinking of corporate governance, incentive structures, and accountability mechanisms. Reflections from a practitioner-scholar perspective.",
    readTime: "13 min read",
    featured: false
  }
];

export const recognitions = [
  {
    id: 1,
    award: "Otto-Hahn Medal",
    organization: "Max Planck Society, Germany",
    year: "2009",
    description: "Prestigious international recognition for outstanding scientific achievement"
  },
  {
    id: 2,
    award: "Inaugural DRUID Best Dissertation Award",
    organization: "Danish Research Unit for Industrial Dynamics",
    year: "2008",
    description: "Global recognition for excellence in innovation and entrepreneurship research"
  },
  {
    id: 3,
    award: "Kauffman Foundation Best Paper Award",
    organization: "Academy of Management, USA",
    year: "2011",
    description: "Recognized for groundbreaking research in entrepreneurship"
  },
  {
    id: 4,
    award: "Selected Young Economist",
    organization: "Lindau Meeting of Nobel Prize Winners in Economics",
    year: "2006",
    description: "Invitation-only recognition of emerging scholars in economics"
  },
  {
    id: 5,
    award: "Fellow",
    organization: "Royal Society of Arts",
    year: "2024",
    description: "Fellowship recognizing contributions to social progress and enlightenment"
  },
  {
    id: 6,
    award: "Steward",
    organization: "Council for Inclusive Capitalism with the Vatican",
    year: "2021",
    description: "Global leadership in sustainable and inclusive economic development"
  }
];

export const institutions = [
  {
    name: "Kingston University London",
    logo: "/api/placeholder/120/60",
    url: "https://www.kingston.ac.uk"
  },
  {
    name: "University of Southampton",
    logo: "/api/placeholder/120/60",
    url: "https://www.southampton.ac.uk"
  },
  {
    name: "Max Planck Society",
    logo: "/api/placeholder/120/60",
    url: "https://www.mpg.de"
  },
  {
    name: "Harvard Business School",
    logo: "/api/placeholder/120/60",
    url: "https://www.hbs.edu"
  },
  {
    name: "Council for Inclusive Capitalism",
    logo: "/api/placeholder/120/60",
    url: "https://www.inclusivecapitalism.com"
  },
  {
    name: "Royal Society of Arts",
    logo: "/api/placeholder/120/60",
    url: "https://www.thersa.org"
  }
];

export const metrics = [
  {
    number: "20+",
    label: "Years Global Experience",
    description: "Spanning academia, government, and enterprise advisory"
  },
  {
    number: "60+",
    label: "Peer-Reviewed Publications",
    description: "In leading international journals"
  },
  {
    number: "10+",
    label: "PhD Candidates Supervised",
    description: "Now leading academics and industry experts"
  },
  {
    number: "4",
    label: "Continents of Impact",
    description: "Europe, Americas, Asia, Africa"
  },
  {
    number: "100+",
    label: "Strategic Engagements",
    description: "With governments, enterprises, and institutions"
  }
];